/* eslint complexity: ["error", {max: 9}] -- complex logout orchestration */
/* eslint max-lines: ["error", {max: 255}] -- complex logout orchestration */
/* eslint max-lines-per-function: ["error", {max: 212}] -- complex logout orchestration */
import { useCallback } from "react";

import isNil from "lodash/isNil";
import { Platform } from "react-native";

import { LogoutReason } from "@/constants/Login";
import { DeviceType } from "@/constants/User";
import { clearAllWebSessionRouteParams } from "@/crossPlatform/expo-router";
import { useDeepLinkingContext } from "@/hooks/deepLinking/useDeepLinkingContext";
import useAuth from "@/hooks/useAuth";
import useHelpCenter from "@/hooks/useHelpCenter";
import usePushNotifications from "@/hooks/usePushNotifications";
import useSendbirdSDK from "@/hooks/useSendbirdSDK";
import useUserIdentitySync from "@/hooks/useUserIdentitySync";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useResetApiStateMutation } from "@/store/api";
import {
  useRevokeAuthTokenMutation,
  useUpdateUserDeviceLoginStateMutation,
} from "@/store/api/user";
import { useDeleteWaitingRoomAssessmentMutation } from "@/store/api/waitingRoom";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { useResetDispatch } from "@/store/reducers/reset";
import { toMs } from "@/utils/date.util";
import { isWeb } from "@/utils/platform.util";
import { createTimeoutPromise, isNotNilNotEmpty } from "@/utils/shared.utils";
import {
  clearWaitingRoomReservation,
  getWaitingRoomReservation,
} from "@/utils/waitingRoomStorage.util";

const HelpCenterLogoutTimeoutMs = toMs({ seconds: 5 });
const RevokeAuthTokenTimeoutMs = toMs({ seconds: 5 });

// NOTE: this hook assumes it's being called ONLY from the LoginPage
// this is to ensure that all rendered components (and associated RTKQuery subscriptions)
// are UNMOUNTED by the time this runs, to prevent loops of API retries/component renders
const useOnlyFromLoginPageLogout = () => {
  const { reset: resetAnalytics } = useAnalytics();
  const { externalId: appUserExternalId } = useAppUserSelector();
  const { resetAllReduxSlices } = useResetDispatch();
  const [resetApiState] = useResetApiStateMutation();
  const [revokeAuth0Token] = useRevokeAuthTokenMutation();
  const { hasValidCredentials, isAuthenticated, preLoginCleanup } = useAuth();
  const { captureException, captureMessage } = useAppMonitor();
  const [updateUserDeviceLoginState] = useUpdateUserDeviceLoginStateMutation();
  const sendbirdSDK = useSendbirdSDK();
  const { pushToken } = usePushNotifications();
  const { logoutHelpCenter } = useHelpCenter();
  const { resetUserIdentity } = useUserIdentitySync();
  const { resetDeepLinkingOnLogout } = useDeepLinkingContext();
  const [deleteWaitingRoomAssessment] =
    useDeleteWaitingRoomAssessmentMutation();

  // NOTE: all promises are .catch()d, to ensure that execution continues forward even in case of error
  // this is because resetApiState() will wipe all relevant api state regardless of any errors here
  const updateEquipLoginState = useCallback(
    async ({ reason }: { reason: LogoutReason }) => {
      if (!isAuthenticated || !isNotNilNotEmpty(appUserExternalId)) {
        return;
      }

      if (reason !== LogoutReason.UserPressedLogout) return;

      // inform equip's user API that user is no longer logged-in; used for push notification purposes
      await updateUserDeviceLoginState({
        isLoggedIn: false,
        os: Platform.OS,
        type: DeviceType.Mobile,
      }).catch(() => {
        captureMessage("Failure with updateUserDeviceLoginState");
      });
    },
    [
      appUserExternalId,
      captureMessage,
      isAuthenticated,
      updateUserDeviceLoginState,
    ],
  );

  const resetAuth = useCallback(
    async ({
      onClearCredentialsFailed,
      onRevokeAuthTokenFailed,
    }: {
      onClearCredentialsFailed: () => void;
      onRevokeAuthTokenFailed: () => void;
    }) => {
      // clear the credentials from the auth0 client's local cache
      // note: this does NOT perform a logout from the auth0 API, nor perform any API calls
      //
      // clearSession() would do such a logout, and also invoke clearCredentials()
      // however as per JN-865, clearSession() is fundamentally broken in the react-native-auth0 SDK for now
      // and would also needlessly trigger an iOS "Auth0 would like to sign in" pop up on logout
      const { data: hasCredentials } = await hasValidCredentials();

      // on web, ALWAYS revoke if have credentials;
      // the different login/logout flow for web means this hook is ONLY invoked when we want the token revoked
      if (hasCredentials && (isAuthenticated || isWeb())) {
        // Trigger (via user-service) an auth0 management API call to revoke refresh tokens.
        // It is important this happens BEFORE credentials are cleared, as the auth credentials
        // are used to authorize the revoke call.
        try {
          await Promise.race([
            revokeAuth0Token(),
            createTimeoutPromise({
              resolveValue: undefined,
              shouldReject: true,
              timeoutMs: RevokeAuthTokenTimeoutMs,
            }),
          ]);
        } catch (err) {
          onRevokeAuthTokenFailed();
          captureException(err, {
            context: {
              extra: {
                scenario: "Failure with revoking Auth0 token",
              },
            },
          });
        }
      }

      // on web, clearWebSession is used instead of clearCredentials
      if (!isWeb() && hasCredentials) {
        // even if !isAuthenticated, if there are valid credentials, they should be wiped
        // see: JN-1524 for android/reinstallation issues where this might occur
        const response = await preLoginCleanup();
        if (response?.error) {
          onClearCredentialsFailed();
        }
      }
    },
    [
      captureException,
      hasValidCredentials,
      isAuthenticated,
      preLoginCleanup,
      revokeAuth0Token,
    ],
  );

  const resetReduxStores = useCallback(async () => {
    // reset ALL redux store reducers/slices/state back to initial state, as if the app were starting for the very first time.
    // NOTE: there are redux reducers that DO not get reset and SHOULD not be reset
    // ie: login (which keeps Remember Me toggled), appStartup: which indicates whether this app session's startup initialization is completed
    // all redux values will automatically be cleared when the app is closed
    resetAllReduxSlices();

    // on web, also clear sessionStorage-backed route params (HIPAA wrapper requirement)
    if (isWeb()) {
      clearAllWebSessionRouteParams();
    }

    // once redux is reset, isAuthenticated == false, so hooks imported by this hook that skip: !isAuthenticated
    // will now be safely guarded to not re-fire, when their caches are reset by resetApiState

    // NOTE: no .catch() as this IS the failsafe method, and if this fails,
    // that's a dealbreaker with regards to moving execution forward
    await resetApiState();
  }, [resetAllReduxSlices, resetApiState]);

  const deleteWaitingRoomReservationOnLogout = useCallback(async () => {
    const mmkvReservation = getWaitingRoomReservation();
    if (isNil(mmkvReservation)) return;
    await deleteWaitingRoomAssessment(mmkvReservation).catch(() =>
      captureMessage("Failure deleting waiting room assessment on logout"),
    );
    clearWaitingRoomReservation();
  }, [captureMessage, deleteWaitingRoomAssessment]);

  const logout = useCallback(
    async ({
      onClearCredentialsFailed,
      onIntercomLogoutFailed,
      onRevokeAuthTokenFailed,
      reason,
    }: {
      onClearCredentialsFailed: () => void;
      onIntercomLogoutFailed: () => void;
      onRevokeAuthTokenFailed: () => void;
      reason: LogoutReason;
    }): Promise<void> => {
      try {
        // ensure deep linking, and its routing, is disabled *before* logout process, to prevent mid-logout deep link routing
        resetDeepLinkingOnLogout();
        // Execute help center (Intercom) logout first for security; do not block forever if it hangs.
        // If this times out or fails, call onIntercomLogoutFailed so the login screen can show
        // restart-required UI and Intercom is not used until app restart (HIPAA).
        await Promise.race([
          logoutHelpCenter(),
          createTimeoutPromise({
            resolveValue: undefined,
            shouldReject: true,
            timeoutMs: HelpCenterLogoutTimeoutMs,
          }),
        ]).catch((err) => {
          onIntercomLogoutFailed();
          captureException(err);
          captureMessage(
            "Failure with logging out of help center (timeout or error)",
          );
        });
        await deleteWaitingRoomReservationOnLogout();
        await updateEquipLoginState({ reason });
        await resetAuth({ onClearCredentialsFailed, onRevokeAuthTokenFailed });
        await resetReduxStores();

        // reset user-analytics (Mixpanel) and user-identity (Braze) immediately
        // after Redux state is cleared — before any further async operations —
        // to eliminate the window between appUser being blanked and Mixpanel
        // being reset, preventing any risk of the old identity persisting on
        // a shared / QA device before the next user logs in
        resetAnalytics();
        resetUserIdentity();

        await sendbirdSDK
          .disconnectSendbird({
            pushToken,
            shouldUnregisterPushToken:
              reason === LogoutReason.UserPressedLogout,
          })
          .catch(() => captureMessage("Failure with disconnecting Sendbird"));
      } catch (caughtErr) {
        captureException(caughtErr);
        captureMessage("Failure clearing session and logging out");
      }
    },
    [
      captureException,
      captureMessage,
      deleteWaitingRoomReservationOnLogout,
      logoutHelpCenter,
      pushToken,
      resetAnalytics,
      resetAuth,
      resetDeepLinkingOnLogout,
      resetReduxStores,
      resetUserIdentity,
      sendbirdSDK,
      updateEquipLoginState,
    ],
  );

  return {
    logout,
  };
};

export default useOnlyFromLoginPageLogout;
